'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
import Link from '@/components/ui/Link';
import AudioPlayer from '@/components/component_unique/AudioPlayer';
import TweetEmbed from '@/components/component_unique/TweetEmbed';
import InstagramEmbed from '@/components/component_unique/InstagramEmbed';
import VideoEmbed from '@/components/component_unique/VideoEmbed';
import GumletEmbed from '@/components/component_unique/GumletEmbed';
import PdfFile from '@/components/component_unique/PdfFile';
import TableEmbed from '@/components/component_unique/TableEmbed';
import ChipsForm from '@/components/component_form/ChipsForm';
import JustIn from '@/components/component_unique/JustIn';
import VuukleComments from '@/components/ads/VuukleComments';
import YouTubeThumbnail from '@/components/component_unique/YouTubeThumbnail';
import ArticleShareButtons from '@/components/component_unique/ArticleShareButtons';
import FileAttachment from '@/components/component_unique/FileAttachment';
import PhotoCarousel from '@/components/component_unique/PhotoCarousel';
import { getImageUrl } from '@/lib/api';
import { getFocusPointStyle } from '@/lib/componentutills';
import { processArticleLinks } from '@/lib/linkUtils';
import { sanitizeToText } from '@/lib/sanitize';
import { computeImageIndexMap, computeParagraphMap } from '@/lib/articleInlineAds';
import { generateLiveBlogPageSchemaFromStory } from '@/lib/schema';

// Duplicated from ArticleListInfinite.tsx (module-private there) — see the
// identical note in ArticleTextBody.tsx.
const LIVE_BLOG_REFRESH_INTERVAL = 30000;

/** Refreshes the LiveBlogPosting/WebPage/BreadcrumbList <script> tags that
 *  SchemaMarkup rendered at initial page load (same element ids), so search
 *  engines/crawlers re-scanning the live DOM see up-to-date content. */
function updateLiveBlogSchema(story: any) {
  if (!story || typeof document === 'undefined') return;
  try {
    const [webPageSchema, liveBlogSchema, breadcrumbSchema] = generateLiveBlogPageSchemaFromStory(story);
    const set = (id: string, schema: object) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(schema);
    };
    set('dinamani-webpage-schema', webPageSchema);
    set('dinamani-liveblog-schema', liveBlogSchema);
    set('dinamani-breadcrumb-schema', breadcrumbSchema);
  } catch {
    // Schema refresh is a SEO nicety, not user-visible — ignore failures.
  }
}

/** Share popup button for live-blog cards — extracted verbatim from
 *  ArticleListInfinite.tsx (module-private there as LiveBlogCardShare). */
function LiveBlogCardShare(t0) {
  const $ = _c(8);
  const {
    slug,
    cardTitle
  } = t0;
  const [open, setOpen] = useState(false);
  slug ? `https://www.dinamani.com/${slug}` : "";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(_temp);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <button type="button" onClick={t1} className="p-1.5 text-4D4D4D hover:text-1B1B1B" aria-label="Share this update"><svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor"><path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" /></svg></button>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== cardTitle || $[3] !== open || $[4] !== slug) {
    t3 = open && <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-DCDCDC rounded shadow-md p-1"><ArticleShareButtons slug={slug} title={cardTitle} className="!p-1 !gap-1" /></div>;
    $[2] = cardTitle;
    $[3] = open;
    $[4] = slug;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <div className="relative">{t2}{t3}</div>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function _temp(v) {
  return !v;
}
const TAMIL_MONTHS = ['ஜனவரி', 'பிப்ரவரி', 'மார்ச்', 'ஏப்ரல்', 'மே', 'ஜூன்', 'ஜூலை', 'ஆகஸ்ட்', 'செப்டம்பர்', 'அக்டோபர்', 'நவம்பர்', 'டிசம்பர்'];
function formatCardTimestamp(timestamp: number): string {
  const d = new Date(timestamp);
  const h = d.getHours() % 12 || 12;
  const m = d.getMinutes().toString().padStart(2, '0');
  const ampm = d.getHours() >= 12 ? 'pm' : 'am';
  return `${h}:${m} ${ampm}, ${d.getDate()} ${TAMIL_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}
export interface ArticleLiveBlogBodyProps {
  story: any;
  index: number;
  activeAdArticleIndex: number;
  category: string;
  sectionUrl: string;
  imageUrl: string;
  imageAttribution: string;
  updatedAt: string;
  showAudioPlayer: boolean;
  promotionBlock: React.ReactNode;
  renderInlineAd?: (globalIdx: number, kind?: 'text' | 'image') => React.ReactNode;
  showRelatedStory?: boolean;
  showLinkedStories?: boolean;
  showStoryTags?: boolean;
  showComments?: boolean;
  /** Whether to render this component's own headline/category/hero-image
   *  block, which duplicates ArticleHeroSection's title/description/heroImage
   *  fields. The LIVE indicator, author/date/share row, and the cards
   *  timeline itself are NOT gated by this — they're the template's actual
   *  content (and status), not a header duplicate, same convention as
   *  ArticlePhotoBody's carousel. */
  showHero?: boolean;
}

/**
 * The `live-blog` article body — extracted from the `story['story-template']
 * === 'live-blog'` branch in src/app/api/articles/ArticleListInfinite.tsx.
 * Unlike every other template body, this one polls for updates: live-blog
 * stories get new/edited cards pushed from the CMS while readers have the
 * page open, so this component re-fetches the story every
 * LIVE_BLOG_REFRESH_INTERVAL and merges in changed cards — matching the
 * legacy polling loop, just scoped to this component's own local state
 * instead of the whole infinite-scroll list's state.
 */
export default function ArticleLiveBlogBody({
  story: initialStory,
  index,
  activeAdArticleIndex,
  category,
  sectionUrl,
  imageUrl,
  imageAttribution,
  updatedAt,
  showAudioPlayer,
  promotionBlock,
  renderInlineAd,
  showRelatedStory = true,
  showLinkedStories = true,
  showStoryTags = true,
  showComments = true,
  showHero = true
}: ArticleLiveBlogBodyProps) {
  const [story, setStory] = useState(initialStory);
  const storyRef = useRef(story);
  storyRef.current = story;
  useEffect(() => {
    if (!initialStory?.['is-live-blog'] || !initialStory?.slug) return;
    const slug = initialStory.slug;
    const poll = async () => {
      try {
        const response = await fetch(`/api/articles?slug=${encodeURIComponent(slug)}&liveblog=true`, {
          cache: 'no-store'
        });
        if (!response.ok) return;
        const data = await response.json();
        const newCards = data?.story?.cards;
        if (!newCards) return;
        const current = storyRef.current;
        const currentCards = current?.cards || [];
        const hasCardCountChange = newCards.length !== currentCards.length;
        const hasStoryUpdate = data.story['content-updated-at'] !== current?.['content-updated-at'] || data.story['last-published-at'] !== current?.['last-published-at'];
        let hasCardUpdate = false;
        for (let i = 0; i < newCards.length; i++) {
          const newCard = newCards[i];
          const oldCard = currentCards[i];
          if (!oldCard || newCard?.['card-updated-at'] !== oldCard?.['card-updated-at'] || newCard?.['card-added-at'] !== oldCard?.['card-added-at'] || newCard?.id !== oldCard?.id) {
            hasCardUpdate = true;
            break;
          }
        }
        if (!hasCardCountChange && !hasCardUpdate && !hasStoryUpdate) return;
        const merged = {
          ...current,
          ...data.story,
          cards: newCards
        };
        setStory(merged);
        updateLiveBlogSchema(merged);
      } catch {
        // Silently ignore — next interval tick retries.
      }
    };
    poll();
    const intervalId = setInterval(poll, LIVE_BLOG_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
    // Only re-run if the story identity itself changes (client-side nav to a
    // different live-blog) — not on every local `story` state update, or
    // this would tear down and restart the interval on every poll.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStory?.slug, initialStory?.['is-live-blog']]);
  const tagNames = story.tags?.map((tag: any) => ({
    name: tag.name,
    slug: tag.slug
  }));
  const inlineAlsoReadIds = new Set<string>();
  story.cards?.forEach((card: any) => {
    card['story-elements']?.forEach((el: any) => {
      if (el.subtype === 'also-read') {
        const id = el.metadata?.['linked-story-id'] || el['linked-story-id'];
        if (id) inlineAlsoReadIds.add(id);
      }
    });
  });
  return <>
      {showHero && <>
          <div className="p-4 border-b border-DCDCDC">
            {category && <Link href={sectionUrl || '#'}>
                <h2 className="text-16-tamil-600 text-012061 mb-2 hover:underline cursor-pointer">{category}</h2>
              </Link>}
            <h1 className="text-24-tamil-700 text-1B1B1B mb-2">{story.headline}</h1>
            {story.subheadline && <h2 className="text-18-tamil-400 text-4D4D4D">{story.subheadline}</h2>}
          </div>
          {imageUrl && <div className="border-b border-DCDCDC">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={imageUrl} alt={story.headline || 'Live Blog'} className="w-full aspect-[16/9] max-h-[475px] object-cover" style={getFocusPointStyle(story['hero-image-metadata'])} loading="lazy" decoding="async" />
              {(story['hero-image-caption'] || imageAttribution) && <div className="p-2 text-12-tamil-400 sm:text-16-tamil-400">
                  <p>
                    {story['hero-image-caption'] && <span className="text-12-tamil-600 text-black">{story['hero-image-caption']}</span>}
                    {story['hero-image-caption'] && imageAttribution && ' - '}
                    {imageAttribution && <span style={{
              color: '#00000099'
            }}>{imageAttribution}</span>}
                  </p>
                </div>}
            </div>}
        </>}

      {promotionBlock}
      {showAudioPlayer && <AudioPlayer />}
      {story['embed-js'] && <div className="border-b border-DCDCDC p-4">
          {(() => {
        try {
          const decoded = atob(story['embed-js']);
          if (decoded.includes('twitter-tweet') || decoded.includes('platform.twitter.com')) {
            return <TweetEmbed embedJs={story['embed-js']} />;
          }
          if (decoded.includes('instagram-media') || decoded.includes('instagram.com')) {
            return <InstagramEmbed embedJs={story['embed-js']} />;
          }
        } catch {
          if (story['embed-js'].includes('twitter-tweet') || story['embed-js'].includes('platform.twitter.com')) {
            return <TweetEmbed embedJs={story['embed-js']} />;
          }
          if (story['embed-js'].includes('instagram-media') || story['embed-js'].includes('instagram.com')) {
            return <InstagramEmbed embedJs={story['embed-js']} />;
          }
        }
        return <GumletEmbed embedJs={story['embed-js']} />;
      })()}
        </div>}
      {story['embed-url'] && <div className="border-b border-DCDCDC">
          <YouTubeThumbnail embedUrl={story['embed-url']} thumbnailUrl={imageUrl} title={story.headline} />
        </div>}

      <div className="p-4 space-y-4">
        {/* afterParagraph: 0 means "before the first paragraph" — see buildParagraphAdPlan. */}
        {renderInlineAd?.(-1)}
        {(() => {
        const paraMap = computeParagraphMap(story);
        const imageMap = computeImageIndexMap(story);
        return story.cards?.map((card: any, cardIdx: number) => {
          const cardTimestamp = card['card-event-at'] || card['card-added-at'];
          const titleElement = card['story-elements']?.find((el: any) => el.type === 'title');
          return <div key={card.id || cardIdx}>
                <div className="relative pl-4 pb-4 mb-4 border-b border-DCDCDC">
                  <div className="absolute top-0 right-0">
                    <LiveBlogCardShare slug={story.slug} cardTitle={titleElement?.text || story.headline} />
                  </div>
                  {cardTimestamp && <div className="text-12-tamil-500 text-4D4D4D mb-2">{formatCardTimestamp(cardTimestamp)}</div>}
                  {titleElement?.text && <h3 className="text-20-tamil-700 text-1B1B1B mb-3">{titleElement.text}</h3>}
                  {card['story-elements']?.map((element: any, elementIdx: number) => {
                const paraInfo = paraMap[`${cardIdx}_${elementIdx}`];
                const imageIdx = imageMap[`${cardIdx}_${elementIdx}`];
                const isRegularText = element.type === 'text' && element.subtype !== 'summary' && element.subtype !== 'also-read' && element.subtype !== 'quote' && element.subtype !== 'blockquote' && element.subtype !== 'cta' && element.subtype !== 'q-and-a' && element.subtype !== 'bigfact' && element.subtype !== 'blurb' && element.subtype !== 'question' && element.text && !element.metadata?.['promotional-message'];
                return <div key={elementIdx} className="mb-3">
                        {element.type === 'jsembed' && element.subtype === 'tweet' && <TweetEmbed embedJs={element['embed-js']} />}
                        {element.type === 'jsembed' && element.subtype === 'instagram' && <InstagramEmbed embedJs={element['embed-js']} />}
                        {element.type === 'jsembed' && element.subtype !== 'tweet' && element.subtype !== 'instagram' && <VideoEmbed embedJs={element['embed-js']} type={element.type} subtype={element.subtype} />}
                        {element.type === 'youtube-video' && element['embed-url'] && <YouTubeThumbnail embedUrl={element['embed-url']} thumbnailUrl={imageUrl} title={element.title} />}
                        {element.type === 'image' && element['image-s3-key'] && <div className="mb-4">
                            <div className="relative w-full h-[300px] sm:h-[400px] md:h-[470px] bg-gray-100 p-[5px] rounded overflow-hidden">
                              <img src={getImageUrl(element['image-s3-key'])} alt="" className="absolute inset-0 w-full h-full object-cover blur-xl scale-110" aria-hidden="true" loading="lazy" decoding="async" />
                              <img src={getImageUrl(element['image-s3-key'])} alt={element['alt-text'] || element.title || 'Live blog image'} className="relative w-full h-full object-contain" loading="lazy" decoding="async" />
                            </div>
                            {(element.title || element['image-attribution']) && <div className="p-2 text-12-tamil-400 sm:text-16-tamil-400">
                                <p>
                                  {element.title && <span className="text-black">{element.title}</span>}
                                  {element.title && element['image-attribution'] && ' - '}
                                  {element['image-attribution'] && <span style={{
                          color: '#00000099'
                        }}>{element['image-attribution']}</span>}
                                </p>
                              </div>}
                            {imageIdx !== undefined && renderInlineAd?.(imageIdx, 'image')}
                          </div>}
                        {element.subtype === 'summary' && element.text && <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />}
                        {element.type === 'file' && element['content-type'] !== 'application/pdf' && element.url && element['file-name'] && <div className="mb-4">
                            <FileAttachment fileName={element['file-name']} url={element.url} fileSize={element.metadata?.['file-size']} />
                          </div>}
                        {element.type === 'composite' && element.subtype === 'image-gallery' && element['story-elements']?.length > 0 && (element.metadata?.type === 'slideshow' ? <div className="my-4">
                              <PhotoCarousel photos={element['story-elements'].filter((img: any) => img['image-s3-key']).map((img: any) => ({
                      id: img.id,
                      title: img.title,
                      imageS3Key: img['image-s3-key'],
                      imageMetadata: img['image-metadata'],
                      attribution: img['image-attribution']
                    }))} maxHeight={400} />
                            </div> : <div className="my-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {element['story-elements'].map((img: any, imgIdx: number) => img['image-s3-key'] && <div key={img.id || imgIdx}>
                                  <img src={getImageUrl(img['image-s3-key'])} alt={img['alt-text'] || img.title || 'Gallery image'} className="w-full h-[150px] object-cover rounded" loading="lazy" decoding="async" />
                                  {img['image-attribution'] && <p className="text-12-tamil-400 mt-1" style={{
                        color: '#00000099'
                      }}>{sanitizeToText(img['image-attribution'])}</p>}
                                </div>)}
                            </div>)}
                        {element.type === 'composite' && element.subtype === 'references' && element['story-elements']?.length > 0 && <div className="my-4 border border-DCDCDC rounded-lg p-4 bg-gray-50">
                            <h4 className="text-16-tamil-700 text-1B1B1B mb-2">குறிப்புகள்</h4>
                            <ul className="space-y-2 list-disc pl-5">
                              {element['story-elements'].map((ref: any, refIdx: number) => <li key={ref.id || refIdx} className="text-14-tamil-400 text-1B1B1B">
                                  {ref.metadata?.url ? <a href={ref.metadata.url} target="_blank" rel="noopener noreferrer" className="text-012061 hover:underline">
                                      {ref.metadata?.name || 'Reference'}
                                    </a> : <span>{ref.metadata?.name}</span>}
                                  {ref.metadata?.description && <span className="block text-12-tamil-400" style={{
                          color: '#00000099'
                        }}>{ref.metadata.description}</span>}
                                </li>)}
                            </ul>
                          </div>}
                        {element.subtype === 'cta' && element.metadata?.['cta-url'] && <div className="my-4 text-center">
                            <a href={element.metadata['cta-url']} target={element.metadata['open-in-new-tab'] ? '_blank' : '_self'} rel="noopener noreferrer" className="inline-block px-6 py-3 bg-012061 text-white rounded-full text-16-tamil-600 hover:opacity-90 transition-opacity">
                              {element.metadata['cta-title'] || element.text}
                            </a>
                          </div>}
                        {element.subtype === 'q-and-a' && (element.metadata?.question || element.metadata?.answer) && <div className="my-4 space-y-3">
                            {element.metadata?.question && <div style={{
                      backgroundColor: '#E3F2FD',
                      padding: '16px',
                      borderRadius: '8px'
                    }}>
                                <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                                  <span style={{
                          backgroundColor: '#1565C0',
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          fontSize: '16px',
                          flexShrink: 0
                        }}>Q</span>
                                  <div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                          flex: 1
                        }} dangerouslySetInnerHTML={{
                          __html: processArticleLinks(element.metadata.question)
                        }} />
                                </div>
                              </div>}
                            {element.metadata?.answer && <div style={{
                      backgroundColor: '#FFF3E0',
                      padding: '16px',
                      borderRadius: '8px'
                    }}>
                                <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                                  <span style={{
                          backgroundColor: '#E65100',
                          color: 'white',
                          fontWeight: 'bold',
                          minWidth: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          fontSize: '16px',
                          flexShrink: 0
                        }}>A</span>
                                  <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-0" style={{
                          flex: 1
                        }} dangerouslySetInnerHTML={{
                          __html: processArticleLinks(element.metadata.answer)
                        }} />
                                </div>
                              </div>}
                          </div>}
                        {element.subtype === 'question' && element.text && <div className="my-4" style={{
                    backgroundColor: '#E3F2FD',
                    padding: '16px',
                    borderRadius: '8px'
                  }}>
                            <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}>
                              <span style={{
                        backgroundColor: '#1565C0',
                        color: 'white',
                        fontWeight: 'bold',
                        minWidth: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                        fontSize: '16px',
                        flexShrink: 0
                      }}>Q</span>
                              <div className="text-18-tamil-700 text-1B1B1B article-links [&_p]:mb-0" style={{
                        flex: 1
                      }} dangerouslySetInnerHTML={{
                        __html: processArticleLinks(element.text)
                      }} />
                            </div>
                          </div>}
                        {element.subtype === 'bigfact' && (element.metadata?.content || element.text) && <div className="my-6 text-center border-y border-DCDCDC py-6">
                            <div className="text-32-tamil-700 text-012061">{element.metadata?.content}</div>
                            {element.metadata?.attribution && <div className="mt-2 text-14-tamil-400" style={{
                      color: '#00000099'
                    }}>{element.metadata.attribution}</div>}
                          </div>}
                        {element.subtype === 'blurb' && element.text && <div className="my-4 bg-gray-50 rounded-lg p-4 article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-600 [&_blockquote]:italic [&_blockquote]:text-1B1B1B" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />}
                        {element.subtype === 'also-read' && element.text && (() => {
                    const linkedStoryId = element.metadata?.['linked-story-id'] || element['linked-story-id'];
                    const linkedStory = linkedStoryId && story['linked-stories']?.[linkedStoryId] || element.metadata?.['linked-story'];
                    if (linkedStory && linkedStory.slug) {
                      return <a href={`/${linkedStory.slug}`} className="flex flex-col sm:flex-row gap-3 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow my-3 p-4">
                                {linkedStory['hero-image-s3-key'] && <div className="w-full sm:w-[150px] h-[160px] sm:h-[100px] flex-shrink-0">
                                    <img src={getImageUrl(linkedStory['hero-image-s3-key'])} alt={linkedStory.headline} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                  </div>}
                                <div className="p-3 flex-1 flex items-center">
                                  <h4 className="text-16-tamil-700 text-1B1B1B">{linkedStory.headline}</h4>
                                </div>
                              </a>;
                    }
                    return null;
                  })()}
                        {element.subtype === 'quote' && element.text && <div className="my-4 quote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />}
                        {element.subtype === 'blockquote' && element.text && <div className="my-4 blockquote-marks article-links [&_blockquote]:m-0 [&_blockquote]:text-18-tamil-400 [&_blockquote]:italic [&_blockquote]:text-1B1B1B [&_.attribution]:mt-2 [&_.attribution]:block [&_.attribution]:text-14-tamil-400 [&_.attribution]:not-italic [&_.attribution]:text-[#00000099]" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />}
                        {/* Regular text: split by paragraphs and insert ads between them, matching the text body's ad mechanism. */}
                        {isRegularText && paraInfo && paraInfo.paragraphs.length > 0 && paraInfo.paragraphs.map((paraHtml: string, paraIdx: number) => {
                    const globalIdx = paraInfo.startIndex + paraIdx;
                    return <div key={`p-${paraIdx}`}>
                                <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                        __html: processArticleLinks(paraHtml)
                      }} />
                                {renderInlineAd?.(globalIdx)}
                              </div>;
                  })}
                        {isRegularText && (!paraInfo || paraInfo.paragraphs.length === 0) && <div className="text-18-tamil-400 text-1B1B1B article-links [&_p]:mb-4" dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />}
                        {element.text && element.metadata?.['promotional-message'] && <div className="text-14-tamil-400 article-links [&_p]:mb-4" style={{
                    color: '#00000099'
                  }} dangerouslySetInnerHTML={{
                    __html: processArticleLinks(element.text)
                  }} />}
                        {element.type === 'file' && element['content-type'] === 'application/pdf' && <PdfFile fileName={element['file-name']} url={element.url} s3Key={element['s3-key']} />}
                        {element.type === 'data' && element.subtype === 'table' && <TableEmbed data={element.data} />}
                      </div>;
              })}
                </div>
              </div>;
        });
      })()}

        {showLinkedStories && story['linked-stories'] && Object.keys(story['linked-stories']).length > 0 && <div className="mb-6">
            <div className="grid grid-cols-1 gap-4">
              {Object.entries(story['linked-stories']).filter(([key]) => !inlineAlsoReadIds.has(key)).map(([key, linkedStory]: [string, any]) => <a key={key} href={`/${linkedStory.slug}`} className="flex flex-col lg:flex-row gap-3 lg:gap-4 p-4 border border-DCDCDC overflow-hidden hover:shadow-md transition-shadow">
                  {linkedStory['hero-image-s3-key'] && <div className="w-full lg:w-[200px] h-[200px] lg:h-[120px] flex-shrink-0">
                      <img src={getImageUrl(linkedStory['hero-image-s3-key'])} alt={linkedStory.headline} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                    </div>}
                  <div className="p-3 lg:p-4 flex-1 flex items-center">
                    <h4 className="text-16-tamil-700 lg:text-18-tamil-700 text-1B1B1B">{linkedStory.headline}</h4>
                  </div>
                </a>)}
            </div>
          </div>}
      </div>
      {index === 0 && showRelatedStory && <JustIn limit={5} />}
      {showStoryTags && tagNames && tagNames.length > 0 && <div className="border-b border-DCDCDC"><ChipsForm chips={tagNames} /></div>}
      {showComments && Math.abs(index - activeAdArticleIndex) <= 1 && <div style={{
      display: index === activeAdArticleIndex ? 'block' : 'none'
    }} aria-hidden={index !== activeAdArticleIndex}>
          <p style={{
        fontSize: '12px',
        color: '#666',
        lineHeight: 1.6,
        padding: '12px',
        background: '#f9f9f9',
        borderRadius: '6px',
        borderLeft: '3px solid #012061',
        margin: '12px 16px'
      }}>பின்னூட்டத்தில் வெளியாகும் கருத்துகளுக்கு அவற்றைப் பதிவிடுவோரே முழுப் பொறுப்பு; அவை தினமணியின் கருத்துகளைப் பிரதிபலிக்கவில்லை.தனிநபர், சமூகம், மதம் அல்லது நாடு ஆகியவற்றுக்கு எதிராக அவமதிக்கிற அல்லது ஆபாசமான விதத்திலுள்ள எந்தவொரு கருத்தும் இந்திய அரசின் தகவல் தொழில்நுட்பக் கொள்கைப்படி தண்டனைக்குரிய குற்றம். இதுபோன்ற கருத்துகளுக்கு எதிராக உரிய சட்ட நடவடிக்கை எடுக்கப்படும்.</p>

          <div className="border-b border-DCDCDC p-4">
            <VuukleComments key={`vuukle-${story.id}`} articleId={story.id} articleTitle={story.headline || ''} articleUrl={`https://www.dinamani.com/${story.slug}`} articleImage={imageUrl} articleTags={story.tags?.slice(0, 3).map((tag: any) => tag.name).join(',') || category} articleAuthor={story.authors?.[0]?.name || 'Dinamani'} />
          </div>
        </div>}
    </>;
}